import * as React from 'react';
import { useCompanySettings } from '../../../javascript/application/useCompanySettings';
import Filters from '../../external/Filters';
import Search from '../../shared/Search';
import { IFilter } from '../filters/IFilter';
import JobsFilter from './JobsFilter';

interface IProps {
  button: string;
  displayButton: boolean;
  buttonText: string;
  headerTag: string;
  hidden: string;
  homeButtonUrl: string;
  logoTag: string;
  provider: 'vimeo' | 'youtube';
  text: string;
  title: string;
  videoBrightness: string;
  videoId: string;
  displayJobFilters?: boolean;
  jobsPath?: string;
  jobsTarget?: string;
  filters: IFilter[];
  visible_columns: Record<string, boolean>;
}

// Finds a cookie by name
//
function getCookie(name: string) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
}

export default function VideoHero(props: IProps) {
  const {
    provider,
    videoId,
    title,
    text,
    displayButton,
    button,
    buttonText,
    headerTag,
    logoTag,
    videoBrightness,
    hidden,
    homeButtonUrl,
    displayJobFilters,
    jobsPath,
    jobsTarget,
    visible_columns,
    filters,
  } = props;

  const { isYoutubeCookieManagementEnabled } = useCompanySettings();
  const [consentGiven, setConsentGiven] = React.useState<boolean>(false);

  const handleConsent = React.useCallback(() => {
    setConsentGiven(true);
  }, []);

  React.useEffect(() => {
    const cookieConsentCookie = getCookie('cc_cookie');
    const decodedCookieConsentCookie = JSON.parse(decodeURIComponent(cookieConsentCookie || '{}'));

    // Check if the user has already given consent
    //
    const analyticsConsent = decodedCookieConsentCookie?.categories?.includes('analytics');

    if (analyticsConsent || !isYoutubeCookieManagementEnabled) {
      handleConsent();
    }
    document.addEventListener('cookieconsent-analytics', handleConsent);

    return () => {
      document.removeEventListener('cookieconsent-analytics', handleConsent);
    };
  }, [handleConsent, isYoutubeCookieManagementEnabled]);

  const setProvider = () => {
    const darkenVideoBrightness = videoBrightness === 'darken';
    const iframeInlineStyles: React.CSSProperties = { filter: darkenVideoBrightness ? 'brightness(0.622)' : 'none' };
    if (provider === 'youtube') {
      const splitId = videoId.split('/');
      const playlistId = splitId[splitId.length - 1];
      return (
        <iframe
          title="Video Player"
          role="presentation"
          tabIndex={-1}
          className="careers-hero-iframe"
          frameBorder="0"
          height="100%"
          width="100%"
          style={iframeInlineStyles}
          src={
            consentGiven
              ? `${videoId}?autoplay=1&mute=1&controls=0&showinfo=0&modestbranding=1&rel=0&playlist=${playlistId}&loop=1`
              : ''
          }
          allow="autoplay"
        />
      );
    }
    return (
      <iframe
        title="Video Player"
        role="presentation"
        tabIndex={-1}
        className="careers-hero-iframe"
        src={`${videoId}?background=1`}
        width="100%"
        style={iframeInlineStyles}
        height="100%"
        frameBorder="0"
        allow="autoplay; fullscreen"
      />
    );
  };

  const titleTag = () => {
    if (headerTag === 'h3') return <h3 className="careers-hero-block__title">{title}</h3>;
    if (headerTag === 'h2') return <h2 className="careers-hero-block__title">{title}</h2>;
    return <h1 className="careers-hero-block__title">{title}</h1>;
  };

  return (
    <div className="careers-hero-block-container">
      <div className="careers-hero-block--video">{setProvider()}</div>
      <div>
        {!hidden && (
          <div className="external-content-wrap">
            <div className="frow frow--centered frow--gutters">
              <a href={homeButtonUrl}>
                <div className="frow frow--items-center frow--gutters">
                  <span className="careers-hero-block__logo-text">Careers at</span>
                  <span dangerouslySetInnerHTML={{ __html: logoTag }} />
                </div>
              </a>
            </div>
          </div>
        )}
        <div className="external-content-wrap careers-hero-block__content-wrap">
          <div className="careers-hero-block__content">
            <div className="col-1-1 col-sm-6-7 col-md-4-5 col-lg-3-5">
              {titleTag()}
              <p className="careers-hero-block__text">{text}</p>
            </div>
            {displayJobFilters && jobsPath && jobsTarget ? (
              <div className="careers-block__inner">
                <div className="careers-block__content pad-b-16">
                  <div className="frow frow--gutters frow--gutters-md-2x frow--items-center">
                    {visible_columns?.search_column_visible && (
                      <div className="col-flex-grow-1">
                        <Search target={jobsTarget} willUpdateUrl={true} searchPlaceholderText="Search..." />
                      </div>
                    )}
                    <div className="col-flex-grow-1">
                      <Filters
                        filters={filters}
                        isUrlFilterable={true}
                        target={jobsTarget}
                        willUpdateUrl={true}
                        visible_columns={{
                          employment_type_column_visible: visible_columns.employment_type_column_visible ?? false,
                          locations_column_visible: visible_columns.locations_column_visible ?? false,
                          departments_column_visible: visible_columns.departments_column_visible ?? false,
                          divisions_column_visible: visible_columns.divisions_column_visible ?? false,
                          requisition_id_column_visible: visible_columns.requisition_id_column_visible ?? false,
                          structure_custom_group_ones_column_visible:
                            visible_columns.structure_custom_group_ones_column_visible ?? false,
                          workplace_type_column_visible: visible_columns.workplace_type_column_visible ?? false,
                        }}
                      />
                    </div>
                    <div className="col-flex-grow-1">
                      <JobsFilter jobsPath={jobsPath} target={jobsTarget} buttonText={buttonText} />
                    </div>
                  </div>
                </div>
              </div>
            ) : displayButton ? (
              <span dangerouslySetInnerHTML={{ __html: button }} />
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
